import { faCheck, faThumbtack } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import truncate from "lodash-es/truncate";
import React from "react";

import { blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { ANONYMOUS_POST_HELP_LINK } from "@/mk2/apps/forum/constants";
import { postDetailUrl, topicUrl } from "@/mk2/apps/forum/helpers";
import { PostEntity } from "@/mk2/apps/forum/schemas";
import { isUncategorizedPost } from "@/mk2/apps/forum/utils";
import { Emojify } from "@/mk2/components/Emojify";
import { Link } from "@/mk2/components/Link";
import { postDeletedByToText } from "@/mk2/helpers/deleteable";
import { timeAgo, timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { FORUM_REPORTED, SHOW } from "@/mk/autogenerated/translations/FeedPost.137c33e2bb96a5fd5817bb6634a7f979";
import { upperCaseFirst } from "@/mk/bazaar/common/filters";
import { isAnonymousUser } from "@/mk/bazaar/common/userUtils";

import styles from "./FeedPost.mscss";

interface OwnProps {
    post: PostEntity;
    isMobile: boolean;
    className?: string;
    readonlyPosts: boolean;
    showTopic?: boolean;
    showSticky?: boolean;
    showCreated?: boolean;
    showAuthor?: boolean;
    showCheckMark?: boolean;
    showFullTitle?: boolean;
    alignDateLeft?: boolean;
    complaintsCount?: number;
}

export default class FeedPost extends React.Component<OwnProps> {
    /**
     *  height of post (including border and padding)
     */
    public static height(post: PostEntity, isMobile: boolean): number {
        return post.deletedAt ? 34 : isMobile ? 92 : 68;
    }

    /**
     *  height of post (including margins)
     */
    public static outerHeight(post: PostEntity, isMobile: boolean): number {
        return FeedPost.height(post, isMobile);
    }

    /**
     *  maximal value that outerHeight() might return
     */
    public static outerHeightMax(isMobile: boolean): number {
        return isMobile ? 92 : 68;
    }

    public shouldComponentUpdate(n: OwnProps) {
        const p = this.props;

        return (
            p.post.id !== n.post.id ||
            p.post.comments !== n.post.comments ||
            p.post.sticky !== n.post.sticky ||
            p.post.likeable?.likesCount !== n.post.likeable?.likesCount ||
            p.post.likeable?.isLiked !== n.post.likeable?.isLiked ||
            p.post.deletedAt !== n.post.deletedAt ||
            p.complaintsCount !== n.complaintsCount ||
            p.showCreated !== n.showCreated
        );
    }

    public render() {
        const {
            post,
            complaintsCount,
            className,
            isMobile,
            showTopic,
            showSticky,
            showCreated,
            showAuthor,
            showCheckMark,
            alignDateLeft,
            readonlyPosts,
            showFullTitle,
        } = this.props;

        const height = showFullTitle ? "auto" : FeedPost.height(post, isMobile);
        const maxLength = showFullTitle ? 300 : isMobile ? 75 : 200;
        const isUncategorized = isUncategorizedPost(post);

        return post.deletedAt ? (
            <div
                className={cx(styles.FeedPost, styles["FeedPost--deleted"], className)}
                style={{ height }}
            >
                {!isAnonymousUser(post.author) && (
                    <>
                        <Link
                            className={styles.FeedPost__deletedAuthor}
                            to={
                                isAnonymousUser(post.author)
                                    ? ANONYMOUS_POST_HELP_LINK
                                    : url(blogsProfileUrl, { username: post.author.username })
                            }
                        >
                            {post.author.username}
                        </Link>
                        &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                    </>
                )}
                {postDeletedByToText(post.deletedByRole)}
                &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                <Link to={postDetailUrl(post)}>{SHOW}</Link>
            </div>
        ) : (
            <div
                className={cx(
                    styles.FeedPost,
                    styles["FeedPost--active"],
                    className,
                    complaintsCount && styles["FeedPost--has-complaints"],
                    readonlyPosts && styles["FeedPost--readonlyPost"],
                )}
                style={{ height }}
            >
                <div className={styles.FeedPost__header}>
                    <Link
                        className={cx(
                            styles.FeedPost__header__heading,
                            showFullTitle && styles["FeedPost__header__heading--fullTitle"],
                            !showFullTitle && styles["FeedPost__header__heading--ellipsis"],
                            post.contestEnded && styles["FeedPost__header__heading--ended"],
                        )}
                        to={postDetailUrl(post)}
                    >
                        <h2
                            className={cx(
                                styles.FeedPost__header__heading__text,
                                !showFullTitle &&
                                    styles["FeedPost__header__heading__text--ellipsis"],
                                styles["FeedPost__header__heading__text--nonCoronavirus"],
                            )}
                        >
                            {showSticky && post.sticky && (
                                <FontAwesomeIcon
                                    icon={faThumbtack}
                                    size="lg"
                                    className={styles.FeedPost__header__stickyFa}
                                />
                            )}

                            <Emojify>
                                {upperCaseFirst(truncate(post.heading, { length: maxLength }))}
                            </Emojify>
                        </h2>
                    </Link>

                    {showCheckMark ? (
                        <span className={styles.FeedPost__header__checkmark}>
                            <FontAwesomeIcon icon={faCheck} />
                        </span>
                    ) : readonlyPosts ? (
                        <div className={styles.FeedPost__header__comments}>{post.comments}</div>
                    ) : (
                        <Link
                            className={cx(
                                styles.FeedPost__header__comments,
                                styles["FeedPost__header__comments--link"],
                            )}
                            to={postDetailUrl(post, { page: "last", scrollto: "last" })}
                            rel="nofollow"
                        >
                            {post.comments}
                        </Link>
                    )}
                </div>

                <div className={styles.FeedPost__footer}>
                    <div
                        className={cx(
                            styles.FeedPost__footer__left,
                            alignDateLeft && styles["FeedPost__footer__left--displayNone"],
                        )}
                    >
                        {complaintsCount > 0 && (
                            <Link
                                className={styles.FeedPost__footer__left__reported}
                                to={postDetailUrl(post)}
                            >
                                {complaintsCount}x {FORUM_REPORTED}
                            </Link>
                        )}

                        {complaintsCount > 0 && <span className={styles.FeedPost__sep}>•</span>}

                        {!isUncategorized ? (
                            showTopic ? (
                                <Link
                                    className={styles.FeedPost__footer__left__link}
                                    to={topicUrl(post.topic)}
                                >
                                    <span className={styles.FeedPost__footer__left__link__text}>
                                        {upperCaseFirst(post.topic.name)}
                                    </span>
                                </Link>
                            ) : showAuthor ? (
                                !isAnonymousUser(post.author) ? (
                                    <Link
                                        className={styles.FeedPost__footer__left__link}
                                        to={url(blogsProfileUrl, {
                                            username: post.author.username,
                                        })}
                                    >
                                        {post.author.username}
                                    </Link>
                                ) : null
                            ) : null
                        ) : null}
                    </div>

                    {readonlyPosts ? (
                        <div
                            className={styles.FeedPost__footer__date}
                            title={timeAt(
                                showCreated ? post.created : post.lastMessageTime,
                                null,
                                true,
                            )}
                        >
                            {timeAgo(showCreated ? post.created : post.lastMessageTime, null, true)}
                        </div>
                    ) : (
                        <Link
                            className={cx(
                                styles.FeedPost__footer__date,
                                styles["FeedPost__footer__date--link"],
                            )}
                            to={postDetailUrl(post, { page: "last", scrollto: "last" })}
                            title={timeAt(
                                showCreated ? post.created : post.lastMessageTime,
                                null,
                                true,
                            )}
                            rel="nofollow"
                        >
                            {timeAgo(showCreated ? post.created : post.lastMessageTime, null, true)}
                        </Link>
                    )}
                </div>
            </div>
        );
    }
}
